import { FerryPax, FerryProduct, MandatoryField } from '@/api/Cart/cart.interfaces';
import { Validation } from '@ui/components/FormV2/Validations/Validations.interfaces';
import {
    isDateAfter,
    isDefinedObject,
    isMembershipNumber,
    isName,
    isPassportNumber,
    isRequired,
    isSelected,
} from '@ui/components/FormV2/Validations/functions';
import { getAgeRange, getProductToDate } from '@/api/Cart/cart.helpers';
import { isValidDateOfBirth } from '@/helpers/checkout';
import { AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
import { isNotNil } from '@shared/utils/value';

const MEMBERSHIP_NUMBER_LENGTH = 7;

interface Params {
    pax: FerryPax;
    index: number;
    ageRangeDefinitions: AgeRangeDefinitions;
    toDate: Date;
    product: FerryProduct;
}

interface FormPaxField {
    name: string;
    validation: Validation[];
    field: MandatoryField;
}

export const getProductPaxFields = (
    product: FerryProduct,
    ageRangeDefinitions: AgeRangeDefinitions
): FormPaxField[] => {
    const toDate = getProductToDate(product) ?? new Date();
    return product.Pax.map((pax, index) =>
        getPaxFields({
            pax,
            index,
            ageRangeDefinitions,
            toDate,
            product,
        })
    ).flat();
};

export const getPaxFields = (params: Params): FormPaxField[] => {
    const fields = [
        MandatoryField.Gender,
        MandatoryField.FirstName,
        MandatoryField.LastName,
        MandatoryField.DateOfBirth,
        MandatoryField.Nationality,
        MandatoryField.PassportNumber,
        MandatoryField.PassportExpirationDate,
        MandatoryField.LoyaltyMembership,
        MandatoryField.SpecialNeeds,
    ];
    return fields.map((field) => getPaxField(field, params)).filter(isNotNil);
};

const getPaxField = (field: MandatoryField, params: Params): FormPaxField | undefined => {
    const { pax, index } = params;
    const optionalFields = [MandatoryField.SpecialNeeds];
    const fields = pax.MandatoryFields.concat(optionalFields);
    return fields.includes(field)
        ? {
              field,
              name: `${field}${index}`,
              validation: getFieldValidation(field, params),
          }
        : undefined;
};

const getFieldValidation = (field: MandatoryField, params: Params): Validation[] => {
    switch (field) {
        case MandatoryField.Gender:
            return [isSelected];
        case MandatoryField.FirstName:
        case MandatoryField.LastName:
            return [isRequired, isName];
        case MandatoryField.Nationality:
            return [isDefinedObject];
        case MandatoryField.PassportNumber:
            return [isRequired, isPassportNumber];
        case MandatoryField.PassportExpirationDate:
            return [isDateAfter(params.toDate)];
        case MandatoryField.LoyaltyMembership:
            return [isMembershipNumber(MEMBERSHIP_NUMBER_LENGTH)];
        case MandatoryField.DateOfBirth: {
            const ageRange = getAgeRange(
                params.product,
                params.pax,
                params.ageRangeDefinitions
            );

            return params.toDate
                ? [(date) => isValidDateOfBirth(date, ageRange, params.toDate)]
                : [];
        }
        default:
            return [];
    }
};
